/**
 * Simple MCP App Host implementation using postMessage
 */
export class MCPAppHost {
  private iframe: HTMLIFrameElement;
  private mcpServerUrl: string;
  private contextUrl: string;
  private messageHandler: ((event: MessageEvent) => void) | null = null;
  private onContextUpdate?: (context: any) => void;
  private sessionId?: string;

  constructor(
    mcpServerUrl: string = (import.meta.env.VITE_MCP_SERVER_URL || '/mcp'),
    onContextUpdate?: (context: any) => void,
    sessionId?: string
  ) {
    this.mcpServerUrl = mcpServerUrl;
    const resolvedMcpUrl = new URL(this.mcpServerUrl, window.location.origin);
    this.contextUrl = new URL('/context', resolvedMcpUrl.origin).toString();
    this.iframe = null as any;
    this.onContextUpdate = onContextUpdate;
    this.sessionId = sessionId;
  }

  /**
   * Set up the host for an iframe
   */
  async setupHost(iframe: HTMLIFrameElement): Promise<void> {
    this.iframe = iframe;

    if (!iframe.contentWindow) {
      throw new Error('Iframe content window not available');
    }

    // Set up postMessage listener
    this.messageHandler = async (event: MessageEvent) => {
      // Only handle messages from our iframe
      if (event.source !== iframe.contentWindow) return;

      const message = event.data;
      console.log('Host received message:', message);

      // Handle JSON-RPC format messages from MCP App SDK
      if (message.jsonrpc === '2.0') {
        switch (message.method) {
          case 'ui/initialize':
            // App is initializing - respond with proper host info
            iframe.contentWindow?.postMessage(
              {
                jsonrpc: '2.0',
                id: message.id,
                result: {
                  protocolVersion: '2026-01-26',
                  hostInfo: {
                    name: 'room-booking-host',
                    version: '1.0.0',
                  },
                  hostCapabilities: {
                    serverTools: {},
                    serverResources: {},
                  },
                  hostContext: {
                    theme: 'light',
                    displayMode: 'inline',
                  },
                },
              },
              '*'
            );
            break;

          case 'tools/call':
            // App wants to call a server tool
            await this.handleToolCall(message);
            break;

          case 'resources/read':
            // App wants to read a resource
            await this.handleResourceRead(message);
            break;

          case 'ui/notifications/initialized':
            // App finished initializing - acknowledge
            console.log('App initialized');
            break;

          case 'ui/notifications/size-changed':
            // App size changed - could update iframe height
            console.log('App size changed:', message.params);
            break;

          case 'ui/notifications/message':
            // App sent a message (could be forwarded to chat)
            console.log('App message:', message.params);
            break;

          case 'ui/update-model-context':
            // App is updating model context
            console.log('App updating model context:', message.params);
            if (this.onContextUpdate) {
              this.onContextUpdate(message.params);
            }
            // Forward context to server session (official MCP app flow)
            if (this.sessionId) {
              fetch(this.contextUrl, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  sessionId: this.sessionId,
                  content: message.params?.content,
                  structuredContent: message.params?.structuredContent,
                }),
              }).catch((error) => {
                console.error('Failed to forward context to server:', error);
              });
            }
            // Send acknowledgement back to app
            iframe.contentWindow?.postMessage(
              {
                jsonrpc: '2.0',
                id: message.id,
                result: {},
              },
              '*'
            );
            break;

          default:
            console.log('Unhandled method:', message.method);
        }
      }
    };

    window.addEventListener('message', this.messageHandler);
    console.log('MCP App Host set up');
  }

  /**
   * Handle tool calls from the app
   */
  private async handleToolCall(message: any): Promise<void> {
    const { id, params } = message;
    const { name, arguments: args } = params;

    try {
      // Forward tool call to MCP server with session ID
      const response = await fetch(this.mcpServerUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json, text/event-stream',
          ...(this.sessionId ? { 'X-Session-ID': this.sessionId } : {}),
        },
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: Date.now(),
          method: 'tools/call',
          params: {
            name,
            arguments: args,
          },
        }),
      });

      const data = await this.parseResponse(response);

      // Send response back to app - only include result OR error, not both
      const responseMessage: any = {
        jsonrpc: '2.0',
        id,
      };

      if (data.error) {
        responseMessage.error = data.error;
      } else {
        responseMessage.result = data.result;
      }

      this.iframe.contentWindow?.postMessage(responseMessage, '*');
    } catch (error) {
      // Send error back to app - only error field, no result
      this.iframe.contentWindow?.postMessage(
        {
          jsonrpc: '2.0',
          id,
          error: {
            code: -32603,
            message: error instanceof Error ? error.message : 'Unknown error',
          },
        },
        '*'
      );
    }
  }

  /**
   * Handle resource reads from the app
   */
  private async handleResourceRead(message: any): Promise<void> {
    const { id, params } = message;
    const { uri } = params;

    try {
      // Forward resource read to MCP server
      const response = await fetch(this.mcpServerUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json, text/event-stream',
          ...(this.sessionId ? { 'X-Session-ID': this.sessionId } : {}),
        },
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: Date.now(),
          method: 'resources/read',
          params: { uri },
        }),
      });

      const data = await this.parseResponse(response);

      // Send response back to app - only include result OR error, not both
      const responseMessage: any = {
        jsonrpc: '2.0',
        id,
      };

      if (data.error) {
        responseMessage.error = data.error;
      } else {
        responseMessage.result = data.result;
      }

      this.iframe.contentWindow?.postMessage(responseMessage, '*');
    } catch (error) {
      // Send error back to app - only error field, no result
      this.iframe.contentWindow?.postMessage(
        {
          jsonrpc: '2.0',
          id,
          error: {
            code: -32603,
            message: error instanceof Error ? error.message : 'Unknown error',
          },
        },
        '*'
      );
    }
  }

  /**
   * Parse response - handle both JSON and SSE formats
   */
  private async parseResponse(response: Response): Promise<any> {
    const contentType = response.headers.get('content-type');

    if (contentType?.includes('text/event-stream')) {
      // Handle SSE response
      const text = await response.text();
      // Parse SSE format - extract JSON from data: lines
      const lines = text.split('\n').filter(line => line.startsWith('data: '));
      if (lines.length === 0) {
        throw new Error('No data in SSE response');
      }
      const jsonData = JSON.parse(lines[lines.length - 1].substring(6));
      return jsonData;
    } else {
      // Handle JSON response
      return await response.json();
    }
  }

  /**
   * Clean up the host
   */
  disconnect(): void {
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
      this.messageHandler = null;
    }
  }
}
